import React, { useState } from 'react';
import StylingMockLayout from '../shared/StylingMockLayout';
import {
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  SuccessButton,
  LoadingButton,
  SearchButton,
  SupportButton,
  LinkButton,
  DownloadButton,
  RefreshButton,
  SearchInput
} from 'components/button';
// Import button styles to ensure they're applied
import '../../../components/button/Button.css';
import './StylingMockButtons.css';

/**
 * Styling Mock Buttons Page
 * Showcases all button components with examples and code snippets
 */
const StylingMockButtons = () => {
  const [searchValue, setSearchValue] = useState('');

  return (
    <StylingMockLayout>
      <div className="styling-mock-buttons">
        <div className="styling-mock-section">
          <h2 className="styling-mock-section-title">Button Components</h2>
          <p className="styling-mock-section-description">
            Comprehensive showcase of all button components including sizes, states, and variations.
          </p>
        </div>

        {/* Primary Buttons */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Primary Buttons</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <PrimaryButton size="small">Small Primary</PrimaryButton>
              <PrimaryButton size="medium">Medium Primary</PrimaryButton>
              <PrimaryButton size="large">Large Primary</PrimaryButton>
              <PrimaryButton disabled>Disabled Primary</PrimaryButton>
            </div>
            <div className="styling-mock-code-block">
              {`<PrimaryButton size="small">Small Primary</PrimaryButton>
<PrimaryButton size="medium">Medium Primary</PrimaryButton>
<PrimaryButton size="large">Large Primary</PrimaryButton>
<PrimaryButton disabled>Disabled Primary</PrimaryButton>`}
            </div>
          </div>
        </div>

        {/* Secondary Buttons */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Secondary Buttons</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <SecondaryButton size="small">Small Secondary</SecondaryButton>
              <SecondaryButton size="medium">Medium Secondary</SecondaryButton>
              <SecondaryButton size="large">Large Secondary</SecondaryButton>
              <SecondaryButton disabled>Disabled Secondary</SecondaryButton>
            </div>
            <div className="styling-mock-code-block">
              {`<SecondaryButton size="small">Small Secondary</SecondaryButton>
<SecondaryButton size="medium">Medium Secondary</SecondaryButton>
<SecondaryButton size="large">Large Secondary</SecondaryButton>
<SecondaryButton disabled>Disabled Secondary</SecondaryButton>`}
            </div>
          </div>
        </div>

        {/* Danger Buttons */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Danger Buttons</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <DangerButton size="small">Small Danger</DangerButton>
              <DangerButton size="medium">Medium Danger</DangerButton>
              <DangerButton size="large">Large Danger</DangerButton>
              <DangerButton disabled>Disabled Danger</DangerButton>
            </div>
            <div className="styling-mock-code-block">
              {`<DangerButton size="small">Small Danger</DangerButton>
<DangerButton size="medium">Medium Danger</DangerButton>
<DangerButton size="large">Large Danger</DangerButton>
<DangerButton disabled>Disabled Danger</DangerButton>`}
            </div>
          </div>
        </div>

        {/* Success Buttons */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Success Buttons</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <SuccessButton size="small">Small Success</SuccessButton>
              <SuccessButton size="medium">Medium Success</SuccessButton>
              <SuccessButton size="large">Large Success</SuccessButton>
              <SuccessButton disabled>Disabled Success</SuccessButton>
            </div>
            <div className="styling-mock-code-block">
              {`<SuccessButton size="small">Small Success</SuccessButton>
<SuccessButton size="medium">Medium Success</SuccessButton>
<SuccessButton size="large">Large Success</SuccessButton>
<SuccessButton disabled>Disabled Success</SuccessButton>`}
            </div>
          </div>
        </div>

        {/* Loading Buttons */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Loading Buttons</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <LoadingButton variant="primary" size="small" />
              <LoadingButton variant="primary" size="medium" />
              <LoadingButton variant="primary" size="large" />
              <LoadingButton variant="secondary" size="medium" />
              <LoadingButton variant="danger" size="medium" />
              <LoadingButton variant="success" size="medium" />
            </div>
            <div className="styling-mock-code-block">
              {`<LoadingButton variant="primary" size="small" />
<LoadingButton variant="primary" size="medium" />
<LoadingButton variant="primary" size="large" />
<LoadingButton variant="secondary" size="medium" />
<LoadingButton variant="danger" size="medium" />
<LoadingButton variant="success" size="medium" />`}
            </div>
          </div>
        </div>

        {/* Icon Buttons */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Icon Buttons</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <DownloadButton size="small" />
              <DownloadButton size="medium" />
              <DownloadButton size="large" />
              <DownloadButton disabled />
              <RefreshButton size="small" />
              <RefreshButton size="medium" />
              <RefreshButton size="large" />
              <RefreshButton disabled />
            </div>
            <div className="styling-mock-code-block">
              {`<DownloadButton size="small" />
<DownloadButton size="medium" />
<DownloadButton size="large" />
<DownloadButton disabled />
<RefreshButton size="small" />
<RefreshButton size="medium" />
<RefreshButton size="large" />
<RefreshButton disabled />`}
            </div>
          </div>
        </div>

        {/* Search Components */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Search Components</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <SearchInput 
                placeholder="Search..." 
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <SearchButton>Search</SearchButton>
            </div>
            <div className="styling-mock-code-block">
              {`<SearchInput 
  placeholder="Search..." 
  value={searchValue}
  onChange={(e) => setSearchValue(e.target.value)}
/>
<SearchButton>Search</SearchButton>`}
            </div>
          </div>
        </div>

        {/* Link Buttons */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Link Buttons</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <LinkButton>Read More →</LinkButton>
              <LinkButton disabled>Disabled Link</LinkButton>
            </div>
            <div className="styling-mock-code-block">
              {`<LinkButton>Read More →</LinkButton>
<LinkButton disabled>Disabled Link</LinkButton>`}
            </div>
          </div>
        </div>

        {/* Support Buttons */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Support Buttons</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <SupportButton>Start Chat</SupportButton>
              <SupportButton>Send Email</SupportButton>
              <SupportButton disabled>Disabled Support</SupportButton>
            </div>
            <div className="styling-mock-code-block">
              {`<SupportButton>Start Chat</SupportButton>
<SupportButton>Send Email</SupportButton>
<SupportButton disabled>Disabled Support</SupportButton>`}
            </div>
          </div>
        </div>
      </div>
    </StylingMockLayout>
  );
};

export default StylingMockButtons;

